// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import authFetch from '../utils/authFetch';
import { useQueryClient } from '@tanstack/react-query';
import { subscribeUserToPush } from '../serviceWorkerRegistration';

const AuthContext = createContext(null);
const selectedTenantStorageKey = 'selectedTenant';
// [MODIFIED] Added 'family_member' and 'guardian' to ensure no one is gated out
const ALLOWED_ROLES = ['patient', 'guest_patient', 'tenant_admin', 'system_admin', 'family_member', 'guardian'];

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [patientEpisodes, setPatientEpisodes] = useState([]);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const fetchAndProcessUserProfile = useCallback(async (actionOrigin = "unknown", preloadedData = null) => {
        try {
            const profileData = preloadedData || await authFetch('/api/users/me/profile');
            
            if (profileData && profileData.id) {
                const episodes = profileData.patient_episodes || [];
                
                // Identify if the user is currently in a B2B clinical context
                const activeMembership = profileData.memberships?.find(m => m.status === 'active');
                const isB2BContext = activeMembership && activeMembership.tenant_id !== profileData.master_tenant_id;

                setUser({ 
                    ...profileData, 
                    patient_episodes: episodes,
                    isB2BContext: isB2BContext,
                    activeTenantId: activeMembership?.tenant_id || profileData.master_tenant_id
                });
                
                setPatientEpisodes(episodes); 
                setIsAuthenticated(true);

                if (profileData.memberships && profileData.memberships.length > 0) {
                    const tenantToSet = activeMembership || profileData.memberships[0];
                    localStorage.setItem(selectedTenantStorageKey, JSON.stringify(tenantToSet));
                }
            } else if (actionOrigin === "initial_check") {
                setIsAuthenticated(false);
            }
        } catch (error) {
            console.error(`AuthContext [${actionOrigin}]: Error:`, error.message);
            if (error.status === 401) {
                setIsAuthenticated(false);
                setUser(null);
            }
        }
    }, []);

    useEffect(() => {
        const checkAuthStatus = async () => {
            setIsLoading(true);
            const token = localStorage.getItem('accessToken');
            if (token && !user) {
                await fetchAndProcessUserProfile("initial_check");
            } else if (!token) {
                setIsAuthenticated(false);
            }
            setIsLoading(true); // [FIX] Kept as true until final state resolved
            setIsLoading(false);
        };
        checkAuthStatus();
    }, [fetchAndProcessUserProfile, user]);
    
    const logout = useCallback(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem(selectedTenantStorageKey);
        sessionStorage.clear();
        setUser(null);
        setIsAuthenticated(false);
        queryClient.clear();
        window.location.href = 'https://www.medsai.us'; 
    }, [queryClient]); 

    const login = useCallback(async (loginResponse) => {
        const { access_token, refresh_token, user_profile } = loginResponse || {};
        
        if (!access_token) return false; 

        localStorage.setItem('accessToken', access_token);
        if (refresh_token) localStorage.setItem('refreshToken', refresh_token);

        try {
            await fetchAndProcessUserProfile("post_login_sync", user_profile);
            subscribeUserToPush();
            queryClient.invalidateQueries();
            return true;
        } catch (profileError) {
            logout(); 
            return false;
        }
    }, [queryClient, fetchAndProcessUserProfile, logout]);

    const refreshAuthUser = useCallback(async () => {
        await fetchAndProcessUserProfile("manual_refresh");
    }, [fetchAndProcessUserProfile]);

    const value = useMemo(() => ({
        user, isAuthenticated, isLoading, patientEpisodes, login, logout, authFetch, refreshAuthUser,
    }), [user, isAuthenticated, isLoading, patientEpisodes, login, logout, refreshAuthUser]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) return { isLoading: true, isAuthenticated: false, user: null };
    return context;
};